import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { Play, Square, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  getEngineQueueSnapshot,
  retryEngineQueueJob,
  removeEngineQueueJob,
  type EngineQueueJob,
  type EngineQueueSnapshot,
} from "../services/engineQueueService";

// Aba Engine do painel de Queues (AB#84619): fila de execuções de workflow que o
// Engine publica no apps/service (BullMQ). Métricas por estado + tabela de jobs
// com retry (falhas) e remoção. Polling serializado, com pausa.

const POLL_MS = 5000;
const SNAPSHOT_LIMIT = 50;

const METRIC_KEYS = ["waiting", "active", "delayed", "failed", "completed"] as const;

const STATE_BADGES: Record<string, string> = {
  waiting: "text-amber-600 bg-amber-500/15",
  active: "text-sky-600 bg-sky-500/15",
  delayed: "text-violet-600 bg-violet-500/15",
  failed: "text-red-600 bg-red-500/15",
  completed: "text-emerald-600 bg-emerald-500/15",
};

const formatTime = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export const EngineQueuePanel = () => {
  const { t } = useTranslation();
  const [snapshot, setSnapshot] = useState<EngineQueueSnapshot | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [actingJobId, setActingJobId] = useState<string | null>(null);
  const isPausedRef = useRef(isPaused);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true);
      try {
        setSnapshot(await getEngineQueueSnapshot(SNAPSHOT_LIMIT));
      } catch {
        if (!silent) {
          toast.error(t("assistant.engineQueue.loadError", "Não foi possível carregar a fila do Engine."));
        }
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [t]
  );

  // Polling serializado (recursive setTimeout — nunca sobrepõe requisições).
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      if (!isPausedRef.current) await load(true);
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };

    load().then(() => {
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [load]);

  const filteredJobs = useMemo(() => {
    const jobs = snapshot?.jobs ?? [];
    if (!searchQuery) return jobs;
    const query = searchQuery.toLowerCase();
    return jobs.filter(
      (job) =>
        (job.agentName ?? "").toLowerCase().includes(query) ||
        (job.triggerType ?? "").toLowerCase().includes(query) ||
        job.state.toLowerCase().includes(query) ||
        (job.executionId ?? "").toLowerCase().includes(query) ||
        (job.failedReason ?? "").toLowerCase().includes(query)
    );
  }, [snapshot, searchQuery]);

  const handleRetry = async (job: EngineQueueJob) => {
    setActingJobId(job.id);
    try {
      await retryEngineQueueJob(job.id);
      toast.success(t("assistant.engineQueue.retried", "Job reenfileirado."));
      await load(true);
    } catch {
      toast.error(t("assistant.engineQueue.retryError", "Não foi possível reprocessar o job."));
    } finally {
      setActingJobId(null);
    }
  };

  const handleRemove = async (job: EngineQueueJob) => {
    const confirmed = window.confirm(
      t("assistant.engineQueue.removeConfirm", "Remover este job da fila? Esta ação não pode ser desfeita.")
    );
    if (!confirmed) return;
    setActingJobId(job.id);
    try {
      await removeEngineQueueJob(job.id);
      toast.success(t("assistant.engineQueue.removed", "Job removido."));
      await load(true);
    } catch {
      toast.error(t("assistant.engineQueue.removeError", "Não foi possível remover o job."));
    } finally {
      setActingJobId(null);
    }
  };

  if (isLoading && !snapshot) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <LoadingSpinner size="sm" />
        <p className="text-sm text-muted-foreground">
          {t("assistant.engineQueue.loading", "Carregando fila do Engine...")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6 w-full">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        {METRIC_KEYS.map((key) => (
          <div key={key} className="rounded-lg border bg-card p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {t(`assistant.engineQueue.metrics.${key}`, key)}
              </p>
              {key === "active" && (snapshot?.metrics.active ?? 0) > 0 && (
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              )}
            </div>
            <p className="text-3xl font-bold">{snapshot?.metrics[key] ?? 0}</p>
          </div>
        ))}
      </div>

      {/* Search + pause */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder={t(
            "assistant.engineQueue.searchPlaceholder",
            "Buscar por workflow, trigger, estado ou execução..."
          )}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md bg-background"
        />
        <Button variant="outline" size="sm" onClick={() => setIsPaused((prev) => !prev)}>
          {isPaused ? (
            <>
              <Play className="h-4 w-4 mr-1" />
              {t("logs.resume", "Retomar")}
            </>
          ) : (
            <>
              <Square className="h-4 w-4 mr-1" />
              {t("logs.pause", "Pausar")}
            </>
          )}
        </Button>
        <span className="text-sm text-muted-foreground">
          {isPaused
            ? t("logs.paused", "Pausado")
            : t("assistant.engineQueue.autoRefresh", "Atualização automática a cada 5s")}
        </span>
      </div>

      {/* Jobs table */}
      <div>
        <h3 className="text-sm font-semibold mb-2">
          {t("assistant.engineQueue.jobsTitle", "Jobs de execução de workflow")}
        </h3>
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left">
                <th className="px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">
                  {t("assistant.engineQueue.columns.enqueuedAt", "Criado")}
                </th>
                <th className="px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">
                  {t("assistant.engineQueue.columns.agent", "Agente")}
                </th>
                <th className="px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">
                  {t("assistant.engineQueue.columns.trigger", "Trigger")}
                </th>
                <th className="px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">
                  {t("assistant.engineQueue.columns.state", "Estado")}
                </th>
                <th className="px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">
                  {t("assistant.engineQueue.columns.attempts", "Tentativas")}
                </th>
                <th className="px-3 py-2 font-medium text-xs uppercase tracking-wider text-muted-foreground">
                  {t("assistant.engineQueue.columns.actions", "Ações")}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                    {t("assistant.engineQueue.empty", "Nenhum job na fila.")}
                  </td>
                </tr>
              ) : (
                filteredJobs.map((job) => (
                  <tr key={job.id} className="border-b last:border-b-0 align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                      {formatTime(job.enqueuedAt)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{job.agentName ?? "—"}</div>
                      {job.executionId && (
                        <div className="text-[10px] font-mono text-muted-foreground truncate max-w-[220px]">
                          {job.executionId}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{job.triggerType ?? "—"}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`${STATE_BADGES[job.state] ?? "text-gray-500 bg-gray-500/10"} font-mono text-[10px] font-medium px-1.5 py-0.5 rounded`}
                      >
                        {job.state}
                      </span>
                      {job.failedReason && (
                        <div className="mt-1 text-xs text-red-500 whitespace-pre-wrap break-words max-w-[320px]">
                          {job.failedReason}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{job.attemptsMade}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {job.state === "failed" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRetry(job)}
                          disabled={actingJobId === job.id}
                          title={t("assistant.engineQueue.retry", "Reprocessar")}
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemove(job)}
                        disabled={actingJobId === job.id}
                        title={t("assistant.engineQueue.remove", "Remover")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
